import { useState } from "react";
import { useTranslation } from "react-i18next";
import { mdiHelpCircle } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Input, Modal, Tooltip, Typography } from "antd";

import { usePendingAction } from "../../utils/usePendingAction";
import { validateEmail } from "../../utils/validateEmail";

interface Props {
  open: boolean;
  onClose: () => void;
  onInviteByEmail: (email: string) => Promise<void>;
}

export const TeamAddMemberModal = ({
  open,
  onClose,
  onInviteByEmail,
}: Props) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const { pending: saving, run: runSave } = usePendingAction();

  const handleClose = () => {
    if (saving) return;
    setEmail("");
    setEmailError(null);
    onClose();
  };

  const handleSave = async () => {
    const normalizedEmail = email.trim();
    if (!validateEmail(normalizedEmail)) {
      setEmailError(
        normalizedEmail ? t("team.emailInvalid") : t("team.emailRequired"),
      );
      return;
    }

    await runSave(async () => {
      try {
        await onInviteByEmail(normalizedEmail);
        handleClose();
      } catch {
        // silent
      }
    });
  };

  return (
    <Modal
      open={open}
      title={t("team.addUser")}
      onCancel={handleClose}
      closable={!saving}
      keyboard={!saving}
      mask={{ closable: false }}
      footer={
        <>
          <Button size="large" disabled={saving} onClick={handleClose}>
            {t("team.cancel")}
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={handleSave}
            loading={saving}
          >
            {t("team.invite")}
          </Button>
        </>
      }
    >
      <Input
        type="email"
        autoComplete="email"
        placeholder={t("team.email")}
        value={email}
        status={emailError ? "error" : undefined}
        suffix={
          <Tooltip title={t("team.emailInvitationHint")}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <Icon
                path={mdiHelpCircle}
                size={0.75}
                color="#8c8c8c"
                title={t("team.emailInvitationHint")}
              />
            </span>
          </Tooltip>
        }
        onChange={(event) => {
          setEmail(event.target.value);
          setEmailError(null);
        }}
        onPressEnter={handleSave}
      />
      {emailError && (
        <Typography.Text
          type="danger"
          style={{ display: "block", marginTop: 4 }}
        >
          {emailError}
        </Typography.Text>
      )}
    </Modal>
  );
};
